// src/components/Odontograma/index.tsx
import React, { useState, useEffect } from "react";
import {
  Typography,
  Spin,
  Alert,
  Table,
  Card,
  Row,
  Col
} from "antd";
import "./MedicalReferences.scss";
import { upperTeeth, lowerTeeth } from "./constants/teethData";
import { useOdontograma } from "./hooks/useOdontograma";

import { getMedicalReferencesByPatient } from "../../../api/medicalReferences.api";
import { getPieceById } from "../../../api/piece.api";
import { getFeeById } from "../../../api/fee.api";
import { getPacienteById, Paciente } from "../../../api/paciente.api";
import { getReferenceType } from "../../../api/referenceTypes.api";

interface RefEnriched {
  referenceId: number;
  pacienteName: string;
  piezaName: string;
  feeName: string;
  referenceTypeName: string;
  fecha: string;
  cantidad: number;
  hasCheck: boolean;
  fechaCita: string;
  fechaReferencia: string;
  dentistumId: string;
}

interface Props {
  pacienteId: number;
}

const MedicalReferences: React.FC<Props> = ({ pacienteId }) => {
  const [refs, setRefs] = useState<RefEnriched[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const {
    
    selectAll,
  } = useOdontograma();

  useEffect(() => {
    const fetchAndMark = async () => {
      try {
        setLoading(true);
        // Use the specific endpoint for patient medical references
        const medicalRefs = await getMedicalReferencesByPatient(pacienteId);

        const enriched: RefEnriched[] = await Promise.all(
          medicalRefs.map(async (r) => {
            const [pieza, fee, pacienteData, referenceType] = await Promise.all(
              [
                getPieceById(r.pieceId),
                getFeeById(r.feeId),
                getPacienteById(r.pacienteId),
                getReferenceType(r.referenceTypeId),
              ]
            );
            const p = pacienteData as Paciente;
            return {
              referenceId: r.id, // Use 'id' instead of 'referenceId'
              pacienteName: `${p.nombre1} ${p.apellido1}`,
              piezaName: pieza.pieza,
              feeName: fee.feeName,
              referenceTypeName: referenceType.nombreReferencia,
              fecha: r.fechaReferencia, // Use fechaReferencia instead of fecha
              cantidad: r.cantidad,
              hasCheck: r.hasCheck,
              fechaCita: r.fechaCita,
              fechaReferencia: r.fechaReferencia,
              dentistumId: r.dentistumId,
            };
          })
        );

        setRefs(enriched);

        // **** AQUÍ LLEGA LA LÓGICA NUEVA ****
        enriched.forEach((ref) => {
          const [quadStr, toothStr] = ref.piezaName.split("-");
          const quad = parseInt(quadStr, 10);
          const toothNum = parseInt(toothStr, 10);

          let sliceArr: typeof upperTeeth = [];
          if (quad === 1) sliceArr = upperTeeth.slice(0, 8);
          else if (quad === 2) sliceArr = upperTeeth.slice(8, 16);
          else if (quad === 4) sliceArr = lowerTeeth.slice(0, 8);
          else if (quad === 3) sliceArr = lowerTeeth.slice(8, 16);

          const toothEntry = sliceArr[toothNum - 1];
          if (toothEntry) {
            selectAll(toothEntry.id);
          }
        });
        // **********************************
      } catch (e) {
        console.error(e);
        setError("Error cargando las referencias enriquecidas.");
      } finally {
        setLoading(false);
      }
    };

    fetchAndMark();
  }, [pacienteId, selectAll]);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 50 }}>
        <Spin tip="Cargando datos..." size="large" />
      </div>
    );
  }
  if (error) {
    return <Alert type="error" message={error} showIcon />;
  }

  return (
    <div className="medical-references-container">
      <Card
        bordered={false}
        style={{
          borderRadius: "8px",
          boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
        }}
      >
        <div className="references-header">
          <Typography.Title level={3} style={{ marginBottom: 24 }}>
            Referencias Médicas
          </Typography.Title>
        </div>

        {refs.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Typography.Text type="secondary" style={{ fontSize: "18px" }}>
              No hay referencias médicas registradas
            </Typography.Text>
          </div>
        ) : (
          <>
            {/* Vista móvil con cards */}
            <div className="mobile-only">
              <div className="references-mobile-cards">
                {refs.map((ref, index) => (
                  <Card
                    key={ref.referenceId}
                    size="small"
                    style={{
                      marginBottom: 16,
                      borderLeft: `4px solid #1890ff`,
                      backgroundColor: "#fafafa",
                    }}
                  >
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <Typography.Text strong style={{ color: "#1890ff", fontSize: "18px" }}>
                          Ref #{ref.referenceId}
                        </Typography.Text>
                        <Typography.Text style={{ fontSize: "16px", color: "#8c8c8c" }}>
                          #{index + 1}
                        </Typography.Text>
                      </div>
                      
                      <div style={{ marginBottom: 8 }}>
                        <Typography.Text type="secondary" style={{ fontSize: "16px" }}>
                          Paciente:
                        </Typography.Text>
                        <br />
                        <Typography.Text strong style={{ fontSize: "17px" }}>
                          {ref.pacienteName}
                        </Typography.Text>
                      </div>

                      <Row gutter={[8, 8]}>
                        <Col span={12}>
                          <Typography.Text type="secondary" style={{ fontSize: "16px" }}>
                            Pieza:
                          </Typography.Text>
                          <br />
                          <Typography.Text strong style={{ fontSize: "17px", color: "#1890ff" }}>
                            {ref.piezaName}
                          </Typography.Text>
                        </Col>
                        <Col span={12}>
                          <Typography.Text type="secondary" style={{ fontSize: "16px" }}>
                            Cantidad:
                          </Typography.Text>
                          <br />
                          <Typography.Text style={{ fontSize: "16px" }}>
                            {ref.cantidad}
                          </Typography.Text>
                        </Col>
                      </Row>

                      <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
                        <Col span={12}>
                          <Typography.Text type="secondary" style={{ fontSize: "16px" }}>
                            Fecha Cita:
                          </Typography.Text>
                          <br />
                          <Typography.Text style={{ fontSize: "16px" }}>
                            {new Date(ref.fechaCita).toLocaleDateString("es-ES", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "2-digit"
                            })}
                          </Typography.Text>
                        </Col>
                        <Col span={12}>
                          <Typography.Text type="secondary" style={{ fontSize: "16px" }}>
                            Estado:
                          </Typography.Text>
                          <br />
                          <Typography.Text style={{ 
                            fontSize: "16px", 
                            color: ref.hasCheck ? "#52c41a" : "#faad14" 
                          }}>
                            {ref.hasCheck ? "Verificado" : "Pendiente"}
                          </Typography.Text>
                        </Col>
                      </Row>

                      <div style={{ marginTop: 8 }}>
                        <Typography.Text type="secondary" style={{ fontSize: "16px" }}>
                          Tratamiento:
                        </Typography.Text>
                        <br />
                        <Typography.Text style={{ fontSize: "17px" }}>
                          {ref.feeName}
                        </Typography.Text>
                      </div>

                      <div style={{ marginTop: 8 }}>
                        <Typography.Text type="secondary" style={{ fontSize: "16px" }}>
                          Tipo de Referencia:
                        </Typography.Text>
                        <br />
                        <Typography.Text style={{ fontSize: "17px" }}>
                          {ref.referenceTypeName}
                        </Typography.Text>
                      </div>

                      <div style={{ marginTop: 8 }}>
                        <Typography.Text type="secondary" style={{ fontSize: "16px" }}>
                          Dentista:
                        </Typography.Text>
                        <br />
                        <Typography.Text style={{ fontSize: "17px" }}>
                          {ref.dentistumId}
                        </Typography.Text>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            {/* Vista desktop con tabla optimizada */}
            <div className="desktop-only">
              <div className="references-table-container">
                <Table
                  columns={[
                    {
                      title: "#",
                      key: "index",
                      width: "auto",
                      render: (_, __, index) => (
                        <Typography.Text strong style={{ color: "#1890ff", fontSize: "17px" }}>
                          {index + 1}
                        </Typography.Text>
                      ),
                    },
                    {
                      title: "Ref ID",
                      dataIndex: "referenceId",
                      key: "referenceId",
                      width: "auto",
                      render: (id: number) => (
                        <Typography.Text strong style={{ color: "#1890ff", fontSize: "16px" }}>
                          {id}
                        </Typography.Text>
                      ),
                    },
                    {
                      title: "Paciente",
                      dataIndex: "pacienteName",
                      key: "pacienteName",
                      ellipsis: true,
                      width: "auto",
                      render: (name: string) => (
                        <Typography.Text style={{ fontSize: "17px" }}>
                          {name}
                        </Typography.Text>
                      ),
                    },
                    {
                      title: "Pieza",
                      dataIndex: "piezaName",
                      key: "piezaName",
                      width: "auto",
                      render: (pieza: string) => (
                        <Typography.Text strong style={{ fontSize: "17px", color: "#1890ff" }}>
                          {pieza}
                        </Typography.Text>
                      ),
                    },
                    {
                      title: "Cantidad",
                      dataIndex: "cantidad",
                      key: "cantidad",
                      width: "auto",
                      render: (cantidad: number) => (
                        <Typography.Text style={{ fontSize: "17px" }}>
                          {cantidad}
                        </Typography.Text>
                      ),
                    },
                    {
                      title: "Tratamiento",
                      dataIndex: "feeName",
                      key: "feeName",
                      ellipsis: true,
                      width: "auto",
                      render: (fee: string) => (
                        <Typography.Text style={{ fontSize: "17px" }}>
                          {fee}
                        </Typography.Text>
                      ),
                    },
                    {
                      title: "Tipo",
                      dataIndex: "referenceTypeName",
                      key: "referenceTypeName",
                      ellipsis: true,
                      width: "auto",
                      render: (type: string) => (
                        <Typography.Text style={{ fontSize: "17px" }}>
                          {type}
                        </Typography.Text>
                      ),
                    },
                    {
                      title: "Estado",
                      dataIndex: "hasCheck",
                      key: "hasCheck",
                      width: "auto",
                      render: (hasCheck: boolean) => (
                        <Typography.Text style={{ 
                          fontSize: "16px", 
                          color: hasCheck ? "#52c41a" : "#faad14" 
                        }}>
                          {hasCheck ? "Verificado" : "Pendiente"}
                        </Typography.Text>
                      ),
                    },
                    {
                      title: "Dentista",
                      dataIndex: "dentistumId",
                      key: "dentistumId",
                      width: "auto",
                      render: (dentistumId: string) => (
                        <Typography.Text style={{ fontSize: "17px" }}>
                          {dentistumId}
                        </Typography.Text>
                      ),
                    },
                    {
                      title: "Fecha Cita",
                      dataIndex: "fechaCita",
                      key: "fechaCita",
                      width: "auto",
                      render: (fechaCita: string) => (
                        <Typography.Text style={{ fontSize: "16px" }}>
                          {new Date(fechaCita).toLocaleDateString("es-ES", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "2-digit"
                          })}
                        </Typography.Text>
                      ),
                    },
                    {
                      title: "Fecha Referencia",
                      dataIndex: "fecha",
                      key: "fecha",
                      width: "auto",
                      render: (fecha: string) => (
                        <Typography.Text style={{ fontSize: "16px" }}>
                          {new Date(fecha).toLocaleDateString("es-ES", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "2-digit"
                          })}
                        </Typography.Text>
                      ),
                    },
                  ]}
                  dataSource={refs.map((r) => ({ ...r, key: r.referenceId }))}
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total, range) => 
                      `${range[0]}-${range[1]} de ${total} referencias`,
                    size: "small",
                  }}
                  scroll={{ x: "max-content" }}
                  size="small"
                  tableLayout="auto"
                  style={{ 
                    backgroundColor: "white",
                    borderRadius: "8px"
                  }}
                />
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default MedicalReferences;
